<template>
    <ModalOverlay :use-modal-defaults="useModalOverlayDefaults" :brightness="overlayBrightness">
        <div
            class="modal-content"
            :class="[
                {
                    styling: !useModalOverlayDefaults,
                    'overflow-scroll': overflowScroll,
                    'darker-background': darkerBackground,
                },
                `width-${width}`,
                `height-${height}`,
                `content-padding-${contentPadding}`,
            ]"
        >
            <div v-if="showHeader" class="head">
                <slot name="head">
                    <div class="title">{{ title }}</div>
                    <div class="right-buttons">
                        <KButton
                            :class="['close-button', `${closeButtonVariant}`]"
                            v-if="showCloseButton"
                            @click="onClose"
                            size="icon"
                        >
                            <img src="@/assets/close_modal.svg" alt="Stäng" />
                        </KButton>
                    </div>
                </slot>
            </div>
            <div class="body">
                <slot></slot>
                <div class="right-buttons">
                    <slot name="rightButtons" />
                    <KButton
                        v-if="!showHeader && showCloseButton"
                        :class="['close-button', `${closeButtonVariant}`]"
                        @click="onClose"
                        size="icon"
                    >
                        <img src="@/assets/close_modal.svg" alt="Stäng" />
                    </KButton>
                </div>
            </div>
        </div>
    </ModalOverlay>
</template>

<script setup lang="ts">
    import ModalOverlay from "@/components/generic/ModalOverlay.vue";
    import KButton from "@/components/Button.vue";

    const emit = defineEmits(["close"]);

    withDefaults(
        defineProps<{
            title?: string;
            showCloseButton?: boolean;
            showHeader?: boolean;
            width?: "default" | "wide" | "max" | "full";
            height?: "default" | "max" | "full";
            contentPadding?: "none" | "default";
            useModalOverlayDefaults?: boolean;
            overflowScroll?: boolean;
            closeButtonVariant?: "default" | "large";
            darkerBackground?: boolean;
            overlayBrightness?: "light" | "dark";
        }>(),
        {
            contentPadding: "default",
            height: "default",
            width: "default",
            showHeader: true,
            showCloseButton: false,
            overflowScroll: true,
            useModalOverlayDefaults: false,
            closeButtonVariant: "default",
            darkerBackground: false,
            overlayBrightness: undefined,
        }
    );

    function onClose() {
        emit("close");
    }
</script>

<style lang="scss" scoped>
    @import "@/style/variables";

    .modal-content {
        height: fit-content;
        position: absolute;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: stretch;
        align-content: stretch;
        box-sizing: border-box;
        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;

        &.styling {
            box-shadow: 8px 8px 18px rgba(85, 103, 109, 0.441916);
            background: $color-light;
        }

        &.darker-background {
            background-color: $color-grey-background;

            .right-buttons {
                ::v-deep > * {
                    box-shadow: $box-shadow-floating-button;
                }
            }
        }

        &.width-default {
            width: 770px;
            max-width: 770px;
        }

        &.width-wide {
            width: 953px;
            max-width: 953px;
        }

        &.width-max {
            max-width: calc(100dvw - 2rem);
        }

        &.width-full {
            max-width: 100dvw;
            left: 0;
            right: 0;
        }

        &.height-default {
            max-height: 80vh;
        }

        &.height-full,
        &.height-max {
            height: 100%;
        }

        &.height-max {
            $bottom-margin: 1rem;
            $margin-top-adjustment: 1rem;
            $total-margin: $header-height + $margin-top-adjustment + $bottom-margin;

            top: $header-height + $margin-top-adjustment;
            bottom: $bottom-margin;
            max-height: calc(100dvh - #{$total-margin});
        }

        &.height-full {
            max-height: calc(100dvh - #{$header-height});
            top: $header-height;
            bottom: 0;
        }

        &.content-padding-none {
            .body {
                padding: 0;
            }
        }

        &.content-padding-default {
            .body {
                padding: 42px 96px;
            }
        }

        .head {
            position: relative;
            width: 100%;
            height: 86px;
            padding: 21px 28px;
            display: flex;
            align-items: center;
            background-color: $color-active-dark;

            .title {
                line-height: 42px;
                color: $color-light;
                font-size: 38px;
                margin: auto;
            }
        }

        .right-buttons {
            display: grid;
            align-items: flex-start;
            grid-auto-flow: column;
            grid-gap: 1rem;
            position: absolute;
            right: 1rem;

            > ::v-deep * {
                background-color: $color-grey-background;
            }

            .close-button {
                &.large {
                    $large-button-size: 3rem;

                    width: $large-button-size;
                    height: $large-button-size;
                    max-height: $large-button-size;
                    max-width: $large-button-size;
                    padding: 0.9rem;
                }
            }
        }

        :not(.head) .right-buttons {
            top: 1rem;
        }

        &.overflow-scroll {
            .body {
                overflow: auto;
            }
        }

        .body {
            height: inherit;
            width: inherit;
        }
    }
</style>
